import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';
import { trapFocus } from '@theme/focus';

// EW CUSTOM: header-drawer.liquid renders the drawer content only through the Section Rendering API (eager_loading),
// otherwise a [data-ew-drawer-placeholder]. This loads the content once, on the first intent to open the drawer.
// Depends on Horizon: section-renderer (hydration mode), focus.js, eager_loading in header-drawer.liquid. After a Horizon update: open the mobile drawer once.
const INTENT_EVENTS = ['pointerenter', 'pointerdown', 'focus'];

/**
 * Loads the drawer content with Horizon's keyed hydration (same request as hydrate() in header.liquid), the morph replaces the placeholder.
 * @param {Element} placeholder - The placeholder in the empty drawer panel.
 * @param {HTMLElement} summary - The drawer toggle.
 * @returns {Promise<void>}
 */
async function loadDrawerContent(placeholder, summary) {
  const details = summary.closest('details');
  const panel = placeholder.parentElement;
  const section = placeholder.closest('.shopify-section');

  // Placeholder already replaced, e.g. by hydrate()
  if (!placeholder.isConnected || !details || !panel || !section) return;

  const url = new URL(window.location.href);
  url.searchParams.delete('page');

  panel.setAttribute('aria-busy', 'true');

  try {
    await sectionRenderer.renderSection(normalizeSectionId(section.id), { cache: false, url, mode: 'hydration' });
  } finally {
    panel.removeAttribute('aria-busy');
  }

  // The morph resets aria-expanded; restore it and trap focus in the loaded content if the drawer opened meanwhile
  if (details.classList.contains('menu-open')) {
    summary.setAttribute('aria-expanded', 'true');
    trapFocus(details);
  }
}

/**
 * Loads the drawer content on the first intent event, retries on the next intent if the request failed.
 * @param {Element} placeholder - The placeholder in the empty drawer panel.
 * @param {HTMLElement} summary - The drawer toggle.
 */
function loadOnIntent(placeholder, summary) {
  const controller = new AbortController();
  const onIntent = () => {
    controller.abort();
    loadDrawerContent(placeholder, summary).catch(() => loadOnIntent(placeholder, summary));
  };

  for (const type of INTENT_EVENTS) {
    summary.addEventListener(type, onIntent, { passive: true, signal: controller.signal });
  }
}

for (const placeholder of document.querySelectorAll('[data-ew-drawer-placeholder]')) {
  const summary = placeholder.closest('details')?.querySelector('summary');
  if (summary instanceof HTMLElement) loadOnIntent(placeholder, summary);
}
